import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { zodResolver } from '@hookform/resolvers/zod';
import { useStore } from '@nanostores/react';
import { Loader2 } from 'lucide-react';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import { Checkbox } from "@/components/ui/checkbox";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "@/components/ui/command"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { Check, ChevronsUpDown, X } from "lucide-react"
import { cn } from "@/lib/utils"

type Operator = '>' | '<' | '=' | '>=' | '<=';
type Rule = {
  field: string;
  operator: Operator;
  value: string | number;
};
type RuleCondition = {
  rules: Rule[];
  logic: 'AND' | 'OR';
};

const formSchema = z
  .object({
    workflowName: z.string().min(2, {
      message: 'Workflow name must be at least 2 characters.',
    }),
    isBulk: z.boolean().default(false),
    portfolioIds: z.array(z.string()).optional(),
    condition: z
      .object({
        rules: z.array(
          z.object({
            field: z.string(),
            operator: z.enum(['>', '<', '=', '>=', '<=']),
            value: z.union([z.string(), z.number()]),
          })
        ),
        logic: z.enum(['AND', 'OR']),
      })
      .default({ rules: [], logic: 'AND' }),
  })
  .superRefine((data, ctx) => {
    if (data.isBulk && (!data.portfolioIds || data.portfolioIds.length === 0)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Portfolios are required for bulk processing',
        path: ['portfolioIds'], // This will point the error message to the 'portfolioIds' field
      });
    }
  });




interface Props {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export function NewOrgWorkflowDrawer({ open, setOpen }: Props) {
  const org = useStore($org);
  const user = useStore($user);
  const portfolios = useStore($portfolios);

  // 1. Define your form.
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      workflowName: '',
      isBulk: org?.orgType === 'SMB',
      portfolioIds: [],
      condition: {
        rules: [],
        logic: 'AND'
      }
    },
  });

  // 2. Define a submit handler.
  const [isLoading, setIsLoading] = useState(false);

  // Get user's timezone in IANA format
  const getUserTimezone = (): string => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch (error) {
      console.warn('Could not detect timezone:', error);
      return 'America/New_York'; // fallback
    }
  };

  async function onSubmit(values: z.infer<typeof formSchema>) {
    setIsLoading(true);
    try {
      if (org) {
        const workflowResult = await createOrgWorkflow(
          values.workflowName,
          org?.id,
          values.isBulk,
          values.portfolioIds ?? [],
          {
            rules: values.condition.rules.map(rule => ({
              ...rule,
              value: String(rule.value)
            })),
            logic: values.condition.logic
          },
          getUserTimezone()
        );

        // route to the workflow builder
        $router.open(
          `/organization/${org!.id}/workflows_o/${workflowResult?.workflow?.id}`,
        );
      }
    } catch (error) {
      toast('Error creating new project:', {
        description: '',
        duration: 5000,
      });
    }
    setIsLoading(false);
  }

  const availableFields = [
    { label: 'Days Past Due', value: 'PastDueDays' },
    { label: 'Balance', value: 'Balance' },
    { label: 'Amount Paid', value: 'AmountPaid' },
    { label: 'Amount Due', value: 'AmountDue' },
    // Add other fields from accountReceivables as needed
  ];

  const operators: { label: string; value: Operator }[] = [
    { label: 'Greater than', value: '>' },
    { label: 'Less than', value: '<' },
    { label: 'Equals', value: '=' },
    { label: 'Greater than or equal', value: '>=' },
    { label: 'Less than or equal', value: '<=' },
  ];

  return (
    <Sheet
      open={open}
      onOpenChange={o => {
        form.reset();
        setOpen(o);
      }}
    >
      <SheetContent className="overflow-y-auto sm:max-w-[600px]">
        <SheetHeader>
          <SheetTitle>
            Create new Workflow
            {/* <Badge className="mb-0">BETA</Badge> */}
          </SheetTitle>
          {/* <SheetDescription>
            Create a project for each file that you want to process.
          </SheetDescription> */}
        </SheetHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="mt-4 space-y-8"
          >
            <FormField
              control={form.control}
              name="workflowName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Workflow name</FormLabel>
                  <FormControl>
                    <Input placeholder="Q1 Call Sync Workflow" {...field} />
                  </FormControl>
                  <FormDescription>
                    Enter a workflow name like "Q1 Call Sync Workflow" to help
                    identify this workflow.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            {org?.orgType === 'SMB' && (
              <>
                <FormField
                  control={form.control}
                  name="portfolioIds"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Portfolios</FormLabel>
                      <Popover>
                        <PopoverTrigger asChild>
                          <FormControl>
                            <Button
                              variant="outline"
                              role="combobox"
                              className={cn(
                                "w-full justify-between",
                                !field.value?.length && "text-muted-foreground"
                              )}
                            >
                              {(field.value?.length ?? 0) > 0
                                ? `${field.value?.length} selected`
                                : "Select portfolios"}
                              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                            </Button>
                          </FormControl>
                        </PopoverTrigger>
                        <PopoverContent className="w-full p-0">
                          <Command>
                            <CommandInput placeholder="Search portfolios..." />
                            <CommandEmpty>No portfolio found.</CommandEmpty>
                            <CommandGroup>
                              {portfolios?.map((portfolio) => (
                                <CommandItem
                                  value={portfolio.portfolio.id}
                                  key={portfolio.portfolio.id}
                                  onSelect={() => {
                                    const values = field.value || [];
                                    const newValues = values.includes(portfolio.portfolio.id)
                                      ? values.filter((id) => id !== portfolio.portfolio.id)
                                      : [...values, portfolio.portfolio.id];
                                    field.onChange(newValues);
                                  }}
                                >
                                  <Check
                                    className={cn(
                                      "mr-2 h-4 w-4",
                                      field.value?.includes(portfolio.portfolio.id)
                                        ? "opacity-100"
                                        : "opacity-0"
                                    )}
                                  />
                                  {portfolio.portfolio.name}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </Command>
                        </PopoverContent>
                      </Popover>
                      <div className="flex flex-wrap gap-1 mt-2">
                        {field.value?.map((portfolioId) => {
                          const portfolio = portfolios?.find((p) => p.portfolio.id === portfolioId);
                          return (
                            <Badge
                              key={portfolioId}
                              variant="secondary"
                              className="flex items-center gap-1"
                            >
                              {portfolio?.portfolio.name}
                              <X
                                className="h-3 w-3 cursor-pointer"
                                onClick={() => {
                                  field.onChange(
                                    field.value?.filter((id) => id !== portfolioId)
                                  );
                                }}
                              />
                            </Badge>
                          );
                        })}
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="condition"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Flagging Conditions</FormLabel>
                      <FormControl>
                        <div className="space-y-4">
                          <Select
                            value={field.value.logic}
                            onValueChange={(value) =>
                              field.onChange({ ...field.value, logic: value as 'AND' | 'OR' })
                            }
                          >
                            <SelectTrigger className="w-[200px]">
                              <SelectValue placeholder="Select logic" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="AND">Match ALL conditions (AND)</SelectItem>
                              <SelectItem value="OR">Match ANY condition (OR)</SelectItem>
                            </SelectContent>
                          </Select>

                          {field.value.rules.map((rule, index) => (
                            <div key={index} className="flex gap-2 items-start">
                              <Select
                                value={rule.field}
                                onValueChange={(value) => {
                                  const newRules = [...field.value.rules];
                                  newRules[index] = { ...rule, field: value };
                                  field.onChange({ ...field.value, rules: newRules });
                                }}
                              >
                                <SelectTrigger className="w-[200px]">
                                  <SelectValue placeholder="Select field" />
                                </SelectTrigger>
                                <SelectContent>
                                  {availableFields.map((f) => (
                                    <SelectItem key={f.value} value={f.value}>
                                      {f.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>

                              <Select
                                value={rule.operator}
                                onValueChange={(value) => {
                                  const newRules = [...field.value.rules];
                                  newRules[index] = { ...rule, operator: value as Operator };
                                  field.onChange({ ...field.value, rules: newRules });
                                }}
                              >
                                <SelectTrigger className="w-[200px]">
                                  <SelectValue placeholder="Select operator" />
                                </SelectTrigger>
                                <SelectContent>
                                  {operators.map((op) => (
                                    <SelectItem key={op.value} value={op.value}>
                                      {op.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>

                              <Input
                                type="text"
                                value={rule.value}
                                onChange={(e) => {
                                  const newRules = [...field.value.rules];
                                  newRules[index] = { ...rule, value: e.target.value };
                                  field.onChange({ ...field.value, rules: newRules });
                                }}
                                className="w-[200px]"
                                placeholder="Enter value"
                              />

                              <Button
                                variant="outline"
                                size="icon"
                                onClick={() => {
                                  const newRules = field.value.rules.filter((_, i) => i !== index);
                                  field.onChange({ ...field.value, rules: newRules });
                                }}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          ))}

                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => {
                              const newRules = [
                                ...field.value.rules,
                                { field: '', operator: '>', value: '' },
                              ];
                              field.onChange({ ...field.value, rules: newRules });
                            }}
                          >
                            Add Condition
                          </Button>
                        </div>
                      </FormControl>
                      <FormDescription>
                        Set conditions for when this workflow should be triggered for the selected portfolios/account groupings. Leaving this blank will not trigger any accounts in the selected portfolios/account groupings.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </>
            )}

            <SheetFooter>
              <SheetClose asChild>
                <Button variant="outline">Cancel</Button>
              </SheetClose>
              <Button type="submit" disabled={isLoading}>
                {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Submit
              </Button>
            </SheetFooter>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  );
}
